import classNames from "classnames/dedupe";
import React, { FC } from "react";

import {
  AccordionDetails,
  AccordionItem,
} from "../../../ActionsSelectors/Accordion";
import { ClearAll } from "../../../ActionsSelectors/Select";
import { TCounterBadgeVariant } from "../../../Atoms/CounterBadge";
import { ThemeProvider, useTheme } from "../../../Utility/ThemeProvider";
import { DropdownAlignment, DropdownToggleStrategy } from "../../../Utility";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import DropdownList from "../../../Utility/Dropdown/DropdownList";
import FilterAccordionTitle from "../FilterAccordionTitle/FilterAccordionTitle";
import { FilterDropdownButton } from "../FilterDropdownButton/FilterDropdownButton";
import { IAbstractFilterProps } from "./AbstractFilter.interface";

const AbstractFilter: FC<IAbstractFilterProps> = ({
  opened,
  filterType = "accordion",
  isAccordionExpanded = false,
  variant,
  title,
  subtitle,
  badge,
  classes,
  clearAll,
  children,
  onToggle,
}) => {
  const theme = useTheme(variant);

  const accordionClasses = classNames("filter-accordion", {
    "filter-accordion--light": theme === "light",
  });

  const badgeData = {
    variant: (theme === "dark" ? "blue" : "white") as TCounterBadgeVariant,
    count: badge?.count,
  };

  // If no filters selected don't display 'clear all' footer
  const clearAllData = +badge?.count > 0 ? clearAll : null;

  // Based on the filterType prop assign position to the dropdown on the screen
  const alignment =
    filterType === "dropdown-right"
      ? DropdownAlignment.END
      : DropdownAlignment.START;

  return filterType === "accordion" || filterType === "accordion-sort" ? (
    <ThemeProvider theme={theme}>
      <AccordionItem
        isExpanded={isAccordionExpanded}
        classes={{ root: accordionClasses }}
        onToggle={onToggle}
      >
        <FilterAccordionTitle
          title={title}
          subtitle={subtitle}
          badge={badge && badgeData}
          iconVariant={filterType === "accordion-sort" ? "sort" : "expand"}
        />
        <AccordionDetails classes={{ root: "filter-accordion-body" }}>
          {children}
        </AccordionDetails>
      </AccordionItem>
    </ThemeProvider>
  ) : (
    <Dropdown
      opened={opened}
      classes={classes?.dropdown}
      theme="dark"
      buttonTheme="dark"
      variant="rounded"
      alignment={alignment}
      dropdownButton={
        <FilterDropdownButton
          title={title}
          badgeCount={badge?.count}
          classes={classes?.filterDropdownButton}
        />
      }
      toggleStrategy={DropdownToggleStrategy.HOVER}
    >
      <DropdownList animation="uds-dropdown-toggle" isInFilter>
        {() => (
          <div
            className={classNames(
              "dropdown-menu__content-wrapper dropdown-menu__content-wrapper--with-min-width mt-4",
              {
                "dropdown-menu__content-wrapper--dark": theme === "dark",
              },
            )}
          >
            <div className="dropdown-menu-body">
              <ThemeProvider theme="dark">{children}</ThemeProvider>
            </div>
            {clearAllData && (
              <ClearAll
                text={clearAllData?.clearAllText}
                onClear={clearAllData?.clearAllCallback}
              />
            )}
          </div>
        )}
      </DropdownList>
    </Dropdown>
  );
};

export default AbstractFilter;
